/**
 * Admin: edit an owner's business profile (name / email / phone).
 *
 * The owner-side settings screen uses identity-based /api/auth/* endpoints
 * (which act on the logged-in account), so it can't be reused as-is for an
 * admin acting on another owner. Instead this twin uses the admin endpoint
 * PUT /api/admin/owners/:id. Password reset, activation, and suspension live on
 * the owner detail page; 2FA and Stripe onboarding are owner-personal.
 */
import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useFocusEffect } from 'expo-router';

import { api } from '../../../../src/api/client';
import Card from '../../../../src/components/Card';
import Button from '../../../../src/components/Button';
import Input from '../../../../src/components/Input';
import ContentContainer from '../../../../src/components/ContentContainer';
import { useToast } from '../../../../src/components/Toast';
import { useTheme } from '../../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../../src/utils/theme';

interface OwnerDetail {
  id: string;
  businessName: string | null;
  email: string | null;
  phone: string | null;
}

export default function AdminOwnerSettingsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const o = await api<OwnerDetail>(`/api/admin/owners/${id}`);
      setBusinessName(o.businessName ?? '');
      setEmail(o.email ?? '');
      setPhone(o.phone ?? '');
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const save = async () => {
    if (!businessName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Business name is required' });
      return;
    }
    try {
      setSaving(true);
      await api(`/api/admin/owners/${id}`, {
        method: 'PUT',
        body: { businessName: businessName.trim(), email: email.trim(), phone: phone.trim() },
      });
      toast.show({ type: 'success', title: 'Saved', message: 'Owner profile updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <View style={styles.center}><ActivityIndicator color={colors.primary} /></View>;
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: spacing.md }}>
      <ContentContainer>
        <Text style={styles.adminBanner}>Admin editing owner {id}</Text>
        <Card>
          <Text style={styles.sectionTitle}>Business Profile</Text>
          <Input label="Business Name *" value={businessName} onChangeText={setBusinessName} placeholder="Business name" />
          <Input label="Email" value={email} onChangeText={setEmail} placeholder="owner@example.com" keyboardType="email-address" autoCapitalize="none" />
          <Input label="Phone" value={phone} onChangeText={setPhone} placeholder="555-555-5555" keyboardType="phone-pad" />
          <Button title="Save Changes" onPress={save} loading={saving} style={{ marginTop: spacing.md }} />
          <Text style={styles.note}>
            Password reset, activation, and suspension are on the owner detail page. 2FA and Stripe onboarding are owner-personal and can’t be changed by an admin.
          </Text>
        </Card>
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  adminBanner: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginBottom: spacing.sm },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  note: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.md },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
